/**
 * The network side of a support capture: one line per request this tab makes while capture is
 * on, with its status (or how it failed) and how long it took. It covers every request, not just
 * our API: uploads and downloads go straight from the browser to storage, and a request the load
 * balancer rejects, or whose response CORS blocks, never reaches a backend handler, so this is
 * the only place those show up.
 *
 * No values: no query string, headers or bodies. Our own API (same origin) keeps its path, which
 * holds route names and external ids, with one-time codes and file paths in it replaced; any other
 * host keeps only its host and the path's length, since storage object paths can hold file names.
 */

export type NetworkLogLevel = 'info' | 'warn' | 'error';
export type NetworkLogger = (level: NetworkLogLevel, message: string) => void;

interface NetworkCaptureState {
  log: NetworkLogger;
  fetch: typeof window.fetch;
  open: XMLHttpRequest['open'];
  send: XMLHttpRequest['send'];
}

let state: NetworkCaptureState | null = null;
const xhrRequests = new WeakMap<XMLHttpRequest, { method: string; url: string }>();

// Our own paths that carry a credential or a file name in the path itself: one-time codes from
// email links, and uploaded file paths. Matches the backend's SupportLogs.redactSensitivePaths.
const CODE_IN_PATH = /(^|\/)(confirmEmail|rejectEmailChange|referral\/invitation)\/[^/]{1,512}/g;
const FILE_IN_PATH = /(^|\/)(arbitraryImages|presignedImage|images\/previewExists)\/(.{1,2048})$/;

function redactSensitivePath(path: string): string {
  return path
    .replace(CODE_IN_PATH, (_m, lead: string, route: string) => `${lead}${route}/<code>`)
    .replace(
      FILE_IN_PATH,
      (_m, lead: string, route: string, file: string) => `${lead}${route}/<path(${file.length})>`,
    );
}

export function describeRequestUrl(url: string, origin: string = window.location.origin): string {
  try {
    const parsed = new URL(url, origin);
    return parsed.origin === origin
      ? redactSensitivePath(parsed.pathname)
      : `${parsed.host} path(${parsed.pathname.length})`;
  } catch {
    return 'unparseable url';
  }
}

function report(method: string, url: string, outcome: number | string, startedAt: number) {
  if (!state) return;
  const ms = Math.round(performance.now() - startedAt);
  const failed = typeof outcome === 'string';
  const level: NetworkLogLevel =
    failed || outcome >= 500 ? 'error' : outcome >= 400 ? 'warn' : 'info';
  const result = failed ? `failed: ${outcome}` : String(outcome);
  state.log(
    level,
    `[net] ${method.toUpperCase()} ${describeRequestUrl(url)} -> ${result} (${ms} ms)`,
  );
}

function fetchTarget(input: RequestInfo | URL, init?: RequestInit) {
  if (typeof input === 'string') return { method: init?.method ?? 'GET', url: input };
  if (input instanceof URL) return { method: init?.method ?? 'GET', url: input.href };
  return { method: init?.method ?? input.method, url: input.url };
}

export function isNetworkCaptureActive(): boolean {
  return state !== null;
}

export function startNetworkCapture(log: NetworkLogger): void {
  if (state) return;
  const current: NetworkCaptureState = {
    log,
    fetch: window.fetch,
    open: XMLHttpRequest.prototype.open,
    send: XMLHttpRequest.prototype.send,
  };
  state = current;

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const { method, url } = fetchTarget(input, init);
    const startedAt = performance.now();
    try {
      const response = await current.fetch.call(window, input, init);
      report(method, url, response.status, startedAt);
      return response;
    } catch (error) {
      // An aborted fetch is the caller's choice, but still worth a line: it explains a gap.
      report(method, url, error instanceof Error ? error.name : 'error', startedAt);
      throw error;
    }
  };

  XMLHttpRequest.prototype.open = function open(
    this: XMLHttpRequest,
    method: string,
    url: string | URL,
    ...rest: unknown[]
  ) {
    xhrRequests.set(this, { method, url: String(url) });
    return (current.open as (...args: unknown[]) => void).call(this, method, url, ...rest);
  } as XMLHttpRequest['open'];

  XMLHttpRequest.prototype.send = function send(
    this: XMLHttpRequest,
    body?: Document | XMLHttpRequestBodyInit | null,
  ) {
    const target = xhrRequests.get(this);
    if (target) {
      const startedAt = performance.now();
      let failure: string | null = null;
      const fail = (event: Event) => {
        failure = event.type;
      };
      this.addEventListener('error', fail);
      this.addEventListener('abort', fail);
      this.addEventListener('timeout', fail);
      this.addEventListener('loadend', () => {
        report(target.method, target.url, failure ?? this.status, startedAt);
      });
    }
    return current.send.call(this, body);
  };
}

export function stopNetworkCapture(): void {
  if (!state) return;
  window.fetch = state.fetch;
  XMLHttpRequest.prototype.open = state.open;
  XMLHttpRequest.prototype.send = state.send;
  state = null;
}
